import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, UserPlus, Check, X, Clock, UserMinus } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import Button from './Button';
import Input from './Input';
import HelpTooltip from './HelpTooltip';
import type { LivingTerms } from '../../hooks/useLivingTerms';

interface PartnerInvitation {
  id: string;
  receiverEmail: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  expiresAt: string;
  createdAt: string;
}

interface JoinRequest {
  id: string;
  senderEmail: string;
  senderFirstName?: string;
  senderLastName?: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
}

interface Props {
  terms: LivingTerms;
  dateLocale: string;
  onMessage: (msg: { type: 'success' | 'error'; text: string }) => void;
  /** Appelé quand le lien change (demande acceptée, dissociation) : les réglages du foyer ne sont plus les mêmes. */
  onLinkChanged: () => void;
}

/**
 * Lien avec le partenaire : demandes de jonction reçues, puis invitation (compte
 * solo) ou statut du compte lié avec la dissociation.
 */
export default function PartnerLinkCard({ terms, dateLocale, onMessage, onLinkChanged }: Props) {
  const { t } = useTranslation('profile');
  const { user, setUser } = useAuthStore();
  const hasPartner = user?.hasPartner || false;

  const [inviteEmail, setInviteEmail] = useState('');
  const [sentInvitations, setSentInvitations] = useState<PartnerInvitation[]>([]);
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const fetchInvitationsAndRequests = useCallback(async () => {
    try {
      const [invitesRes, requestsRes] = await Promise.all([
        api.get('/partner/invitations').catch(() => ({ data: { success: false } })),
        api.get('/partner/join-requests').catch(() => ({ data: { success: false } })),
      ]);

      if (invitesRes.data.success) {
        setSentInvitations(invitesRes.data.data || []);
      }
      if (requestsRes.data.success) {
        setJoinRequests(requestsRes.data.data || []);
      }
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchInvitationsAndRequests();
  }, [fetchInvitationsAndRequests]);

  const handleSendInvitation = async () => {
    if (!inviteEmail || isSendingInvite) return;
    setIsSendingInvite(true);
    try {
      const response = await api.post('/partner/invite', { email: inviteEmail });
      if (response.data.success) {
        onMessage({ type: 'success', text: t('couple.inviteSent') });
        setInviteEmail('');
        fetchInvitationsAndRequests();
      }
    } catch (err: any) {
      onMessage({ type: 'error', text: err.response?.data?.message || t('couple.sendError') });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleCancelInvitation = async (id: string) => {
    try {
      await api.delete(`/partner/invite/${id}`);
      fetchInvitationsAndRequests();
    } catch {
      onMessage({ type: 'error', text: t('couple.cancelError') });
    }
  };

  const handleAcceptJoinRequest = async (id: string) => {
    try {
      const response = await api.post(`/partner/join-request/${id}/accept`);
      if (response.data.success) {
        onMessage({ type: 'success', text: t('joinRequests.acceptSuccess') });
        fetchInvitationsAndRequests();
        const userResponse = await api.get('/user/profile');
        if (userResponse.data.success) {
          setUser(userResponse.data.data);
        }
        onLinkChanged();
      }
    } catch {
      onMessage({ type: 'error', text: t('joinRequests.acceptError') });
    }
  };

  const handleRejectJoinRequest = async (id: string) => {
    try {
      await api.post(`/partner/join-request/${id}/reject`);
      fetchInvitationsAndRequests();
    } catch {
      onMessage({ type: 'error', text: t('joinRequests.rejectError') });
    }
  };

  const handleUnlink = async () => {
    setIsUnlinking(true);
    try {
      const response = await api.post('/partner/unlink');
      if (response.data.success) {
        if (response.data.data) {
          setUser(response.data.data);
        }
        onMessage({ type: 'success', text: t('couple.unlinkSuccess') });
        setShowUnlinkModal(false);
        onLinkChanged();
      }
    } catch (err: any) {
      onMessage({ type: 'error', text: err.response?.data?.message || t('couple.unlinkError') });
    } finally {
      setIsUnlinking(false);
    }
  };

  const pendingRequests = joinRequests.filter(r => r.status === 'pending');
  const pendingInvitations = sentInvitations.filter(i => i.status === 'pending');

  return (
    <>
      {/* Demandes de jonction reçues : une action est attendue */}
      {pendingRequests.length > 0 && (
        <div className="card border-primary-200 bg-primary-50">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary-600" />
            {t('joinRequests.title')}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('joinRequests.description', { partner: terms.partnerSingular })}
          </p>
          <div className="space-y-3">
            {pendingRequests.map((req) => (
              <div key={req.id} className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-lg border">
                <div>
                  <p className="font-medium text-gray-900">
                    {req.senderFirstName} {req.senderLastName}
                  </p>
                  <p className="text-sm text-gray-500">{req.senderEmail}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Check className="w-4 h-4" />}
                    onClick={() => handleAcceptJoinRequest(req.id)}
                  >
                    {t('accept', { ns: 'common' })}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<X className="w-4 h-4" />}
                    onClick={() => handleRejectJoinRequest(req.id)}
                  >
                    {t('reject', { ns: 'common' })}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasPartner ? (
        /* Compte lié */
        <div className="card bg-green-50 border-green-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-900">{t('couple.sharedAccount')}</p>
                <p className="text-sm text-green-700">{terms.linkedMessage}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<UserMinus className="w-4 h-4" />}
              onClick={() => setShowUnlinkModal(true)}
            >
              {t('couple.unlink')}
            </Button>
          </div>
        </div>
      ) : (
        /* Inviter un partenaire/colocataire */
        <div className="card" data-tutorial="invite-partner">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary-600" />
            {terms.invitePartner}
            <HelpTooltip helpKey="partnerInvitation" />
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {terms.invitePartnerDescription}
          </p>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendInvitation()}
                placeholder="email@exemple.com"
              />
            </div>
            <Button
              variant="primary"
              leftIcon={<Send className="w-4 h-4" />}
              onClick={handleSendInvitation}
              isLoading={isSendingInvite}
              disabled={!inviteEmail}
            >
              {t('invite', { ns: 'common' })}
            </Button>
          </div>

          {/* Invitations en attente */}
          {pendingInvitations.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700">{t('couple.inviteSentList')}</p>
              {pendingInvitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm text-gray-700 break-all">{inv.receiverEmail}</span>
                    <span className="text-xs text-gray-500">
                      ({t('date.expiresOn', { date: new Date(inv.expiresAt).toLocaleDateString(dateLocale), ns: 'common' })})
                    </span>
                  </div>
                  <button
                    onClick={() => handleCancelInvitation(inv.id)}
                    className="p-1 hover:bg-yellow-100 rounded text-yellow-700"
                    title={t('cancel', { ns: 'common' })}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal dissociation */}
      {showUnlinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-orange-600 mb-4">{t('couple.unlinkTitle')}</h2>
            <p className="text-gray-600 mb-4">
              {t('couple.unlinkMessage', { partner: terms.partnerSingular })}
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-orange-800">
                <strong>{t('couple.unlinkWhatHappens')}</strong>
              </p>
              <ul className="text-sm text-orange-700 mt-2 space-y-1 list-disc list-inside">
                <li>{t('couple.unlinkOwnSpace')}</li>
                <li>{t('couple.unlinkPartnerKeeps', { partner: terms.partnerSingular })}</li>
                <li>{t('couple.unlinkNoSharing')}</li>
              </ul>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="secondary" className="flex-1" onClick={() => setShowUnlinkModal(false)}>
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button variant="danger" className="flex-1" onClick={handleUnlink} isLoading={isUnlinking}>
                {t('couple.unlinkConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
